/**
 * ValidationResults Component
 *
 * Displays the validation results comparing detected items vs expected items for a section.
 * Shows match/mismatch status with visual indicators.
 *
 * Status Indicators:
 * - ✅ Match: Detected quantity matches expected
 * - ❌ Mismatch: Detected quantity differs from expected
 * - ⚠️ Unexpected: Item found but not expected in this section
 * - 🔍 Missing: Expected item not detected
 */

import React from 'react';
import { SectionValidationResult, ItemValidationResult } from '@/types';
import { CheckCircle, XCircle, AlertTriangle, Search } from 'lucide-react';

interface ValidationResultsProps {
  validation: SectionValidationResult;
  className?: string;
  onClose?: () => void;
}

export const ValidationResults: React.FC<ValidationResultsProps> = ({
  validation,
  className = '',
}) => {
  // Helper to get icon and color based on status
  const getStatusDisplay = (status: ItemValidationResult['status']) => {
    switch (status) {
      case 'match':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          label: 'Match',
        };
      case 'mismatch':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Mismatch',
        };
      case 'unexpected':
        return {
          icon: AlertTriangle,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          label: 'Unexpected',
        };
      case 'missing':
        return {
          icon: Search,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          label: 'Missing',
        };
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Validation: {validation.section_name}
        </h3>
        {validation.has_discrepancies ? (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <XCircle className="h-4 w-4 mr-1" />
            Discrepancies Found
          </span>
        ) : (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-4 w-4 mr-1" />
            All Items Match
          </span>
        )}
      </div>

      {/* Validation Items List */}
      <div className="space-y-2">
        {validation.validation_items.map((item) => {
          const display = getStatusDisplay(item.status);
          const Icon = display.icon;

          return (
            <div
              key={item.class_name}
              className={`p-3 rounded-lg border ${display.bgColor} ${display.borderColor}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon className={`h-5 w-5 ${display.color}`} />
                  <div>
                    <div className="font-medium text-gray-900">{item.class_name}</div>
                    <div className="text-sm text-gray-600">
                      Expected: {item.expected} | Detected: {item.detected}
                    </div>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded ${display.color}`}>
                  {display.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Footer */}
      <div className="pt-3 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          {validation.validation_items.filter((i) => i.status === 'match').length} of{' '}
          {validation.validation_items.length} items validated successfully
        </div>
      </div>
    </div>
  );
};

export default ValidationResults;
